import React, { useMemo, useState } from 'react';
import { workflowTestChatEnabled } from '../../../services/api';
import type { WorkflowState } from '../../../types/workflow';
import type { WorkflowTuningChange } from '../../../types/workflowTuning';
import { applyChangesToStates, isApplicableChange } from '../../../utils/workflowTuningSpec';
import {
  busiestRole,
  busiestState,
  changesByState,
  compareFields,
  isBlank,
  parseRoleKey,
  rolesForState,
  type CompareField,
} from '../../../utils/workflowCompare';
import NltTextFieldDiffLines from '../../NltTextFieldDiffLines';
import { Banner, Pill } from './WorkflowFormControls';
import { DefValue } from './WorkflowDefinitionView';
import WorkflowTestChats from './WorkflowTestChats';

export interface WorkflowCompareViewProps {
  integrationId: string;
  /** The graph as currently open in the editor, or null when it has not loaded. */
  states: Record<string, WorkflowState> | null;
  changes: WorkflowTuningChange[];
  loading: boolean;
  /** Set when the proposal was built on a revision the graph has since moved off. */
  staleRevision: string | null;
  /** The run's `base_revision.workflow_version_id`, part of the test chat's position. */
  workflowVersionId?: string;
}

function renderValue(value: unknown, multiline: boolean): React.ReactNode {
  if (isBlank(value)) return <span className="cwc-empty">—</span>;
  if (typeof value === 'string') {
    return multiline ? <pre className="cwc-pre">{value}</pre> : <span className="cwc-inline">{value}</span>;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return <span className="cwc-inline">{String(value)}</span>;
  }
  return (
    <div className="cwc-structured">
      <DefValue value={value} />
    </div>
  );
}

/** One row, side by side, with the changed ones marked rather than merely coloured. */
const CompareRow: React.FC<{ row: CompareField; showDiff: boolean }> = ({ row, showDiff }) => {
  const textDiff =
    showDiff && row.changed && typeof row.before === 'string' && typeof row.after === 'string' && row.multiline;

  return (
    <div className={`cwc-row ${row.changed ? 'cwc-row--changed' : ''}`}>
      <div className="cwc-row__head">
        <span className="cwc-row__label">{row.label}</span>
        {row.added ? (
          <Pill tone="ok">added</Pill>
        ) : row.removed ? (
          <Pill tone="warn">removed</Pill>
        ) : row.changed ? (
          <Pill tone="accent">changed</Pill>
        ) : null}
      </div>
      {textDiff ? (
        <div className="cwc-row__diff">
          <NltTextFieldDiffLines
            oldValue={String(row.before)}
            newValue={String(row.after)}
            mode="pending"
            variant="summary"
          />
        </div>
      ) : (
        <div className="cwc-row__pair">
          <div className="cwc-cell cwc-cell--initial">{renderValue(row.before, row.multiline)}</div>
          <div className="cwc-cell cwc-cell--optimized">{renderValue(row.after, row.multiline)}</div>
        </div>
      )}
    </div>
  );
};

/**
 * Initial ↔ Optimized for the workflow, the counterpart of Config Tuning's
 * "Configuration evaluation": pick a state and a role, see the baseline on the left and
 * what the run proposes on the right.
 *
 * Only `/states/…` changes can be shown here — global sections are not writable through
 * this connector and have no state to hang under; they stay in the changes list.
 */
const WorkflowCompareView: React.FC<WorkflowCompareViewProps> = ({
  integrationId,
  states,
  changes,
  loading,
  staleRevision,
  workflowVersionId,
}) => {
  const applicable = useMemo(() => changes.filter(isApplicableChange), [changes]);
  const counts = useMemo(() => changesByState(applicable), [applicable]);

  const { initialStates, optimizedStates } = useMemo(() => {
    if (!states) return { initialStates: null, optimizedStates: null };
    // The left column writes each change's `before`, so it is the run's own baseline
    // even where the editor has since been touched.
    const initial = applyChangesToStates(
      states,
      applicable.map((change) => ({ path: change.path, after: change.before }))
    ).states;
    const optimized = applyChangesToStates(states, applicable).states;
    return { initialStates: initial, optimizedStates: optimized };
  }, [states, applicable]);

  const stateIds = useMemo(() => {
    if (!optimizedStates) return [];
    const ids = Object.keys(optimizedStates);
    // Touched states first, so the picker opens on something worth reading.
    return ids.sort((a, b) => (counts.get(b) ?? 0) - (counts.get(a) ?? 0) || a.localeCompare(b));
  }, [optimizedStates, counts]);

  // Derived, not stored: `changes` arrive after the graph does, and a stored default
  // chosen from the empty set would stay pinned to an untouched state forever.
  const [pickedState, setPickedState] = useState<string | null>(null);
  const [pickedRole, setPickedRole] = useState<string | null>(null);
  const [showDiff, setShowDiff] = useState(true);

  const stateId = pickedState && stateIds.includes(pickedState) ? pickedState : busiestState(stateIds, counts);

  const initialState = stateId && initialStates ? initialStates[stateId] : undefined;
  const optimizedState = stateId && optimizedStates ? optimizedStates[stateId] : undefined;
  const roles = useMemo(() => rolesForState(initialState, optimizedState), [initialState, optimizedState]);

  const roleKey =
    pickedRole && roles.some((role) => role.key === pickedRole)
      ? pickedRole
      : busiestRole(roles, initialState, optimizedState);

  const rows = useMemo(
    () => compareFields(roleKey, initialState, optimizedState),
    [roleKey, initialState, optimizedState]
  );
  const changedRows = rows.filter((row) => row.changed).length;
  const roleChangeCounts = useMemo(
    () =>
      new Map(
        roles.map((role) => [
          role.key,
          compareFields(role.key, initialState, optimizedState).filter((row) => row.changed).length,
        ])
      ),
    [roles, initialState, optimizedState]
  );

  if (loading) {
    return (
      <p className="cwf-note">
        <i className="fas fa-spinner fa-spin" aria-hidden="true" /> Loading the comparison…
      </p>
    );
  }

  if (!states) {
    return (
      <Banner tone="info" title="The graph is not open">
        <p>Open the workflow graph first — the baseline column is read from it.</p>
      </Banner>
    );
  }

  if (!stateId || stateIds.length === 0) {
    return (
      <Banner tone="info" title="Nothing to compare">
        <p>This proposal touches no state in the graph as it is currently open.</p>
      </Banner>
    );
  }

  const roleKind = parseRoleKey(roleKey).kind;

  return (
    <section className="cwc" data-testid="cwt-compare">
      <header className="cwc__head">
        <div className="cwc__intro">
          <span className="cwc__eyebrow">Compare</span>
          <h3>Workflow evaluation</h3>
          <p>
            The baseline this run started from beside what it proposes. Nothing here is written — applying still goes
            through the graph's own Save flow.
          </p>
        </div>
        {/* `cwf-check` draws the box itself: the app-wide iOS reset strips `appearance`
            and leaves a native checkbox with zero size. */}
        <label className="cwf-check cwf-check--sm cwc__toggle">
          <input type="checkbox" checked={showDiff} onChange={(e) => setShowDiff(e.target.checked)} />
          <span>Line diff on long text</span>
        </label>
      </header>

      {staleRevision ? (
        <Banner tone="warn" title="The graph has moved since this proposal was built">
          <p>
            Untouched fields below are read from the graph as it is now, not from the revision the run pinned
            (<code>{staleRevision.slice(0, 12)}…</code>). Changed fields are still the run's own before and after.
          </p>
        </Banner>
      ) : null}

      <div className="cwc__pickers">
        <label className="cwc__picker">
          <span className="cwc__picker-label">State</span>
          <select
            className="cwf-input cwf-input--sm"
            data-testid="cwc-state"
            value={stateId}
            onChange={(e) => {
              setPickedState(e.target.value);
              // A role chosen for the previous state means nothing here.
              setPickedRole(null);
            }}
          >
            {stateIds.map((id) => {
              const count = counts.get(id) ?? 0;
              return (
                <option key={id} value={id}>
                  {id}
                  {count > 0 ? ` — ${count} changed` : ' — unchanged'}
                </option>
              );
            })}
          </select>
        </label>
        <label className="cwc__picker">
          <span className="cwc__picker-label">Role</span>
          <select
            className="cwf-input cwf-input--sm"
            data-testid="cwc-role"
            value={roleKey}
            onChange={(e) => setPickedRole(e.target.value)}
          >
            {roles.map((role) => {
              const count = roleChangeCounts.get(role.key) ?? 0;
              return (
                <option key={role.key} value={role.key}>
                  {role.label}
                  {count > 0 ? ` — ${count} changed` : ''}
                </option>
              );
            })}
          </select>
        </label>
      </div>

      <div className="cwc__columns">
        <div className="cwc__col cwc__col--initial">
          <span className="cwc__col-label">Initial</span>
          <p className="cwc__col-hint">Baseline workflow</p>
        </div>
        <div className="cwc__col cwc__col--optimized">
          <span className="cwc__col-label">Optimized</span>
          <p className="cwc__col-hint">
            Proposed by this run
            {changedRows > 0 ? (
              <Pill tone="accent">
                {changedRows} changed field{changedRows === 1 ? '' : 's'}
              </Pill>
            ) : null}
          </p>
        </div>
      </div>

      <div className="cwc__rows">
        {rows.length === 0 ? (
          <p className="cwf-note">
            This {roleKind === 'state' ? 'state' : 'agent'} carries no comparable fields in the current graph.
          </p>
        ) : (
          rows.map((row) => <CompareRow key={row.key} row={row} showDiff={showDiff} />)
        )}
      </div>

      {changedRows === 0 ? (
        <p className="cwc__note">
          The run left this {roleKind === 'state' ? 'state' : 'agent'} exactly as it was. Pick another from the pickers
          above — states the run touched are listed first.
        </p>
      ) : null}

      {workflowTestChatEnabled() ? (
        <WorkflowTestChats
          integrationId={integrationId}
          stateId={stateId}
          initialState={initialState}
          optimizedState={optimizedState}
          workflowVersionId={workflowVersionId}
        />
      ) : null}
    </section>
  );
};

export default WorkflowCompareView;
